<script>
  import { _ } from "svelte-i18n";
  import { onMount } from "svelte";
  import { currentPage } from "../../utilities/DataStore";
  import { BASE_URL } from "../../utilities/config.js";
  import { sanitizeAssessmentHtml } from "../../utilities/TextSanitizer.js";
  import Loader from "./report/Loader.svelte";
  import IeltsReportHeader from "./report/IeltsReportHeader.svelte";
  import IeltsReportItemHeader from "./report/IeltsReportItemHeader.svelte";
  import { buildIeltsSpeakingReport } from "../ieltsSkills.mjs";
  import { formatIeltsBand } from "../ieltsReading.mjs";

  export let instanceId;
  export let instanceData;
  // "SCORE" shows the band and answer summary only, "DETAIL" adds the
  // per-answer transcript, recording playback and score.
  export let variant = "SCORE";

  let reportStatus = "LOADING";
  let report = { answers: [], band: null, answeredCount: 0 };

  // Answer sections are expanded by default, same as the other IELTS reports.
  let collapsedAnswers = {};

  // Transcripts come from the per-result endpoint (the recognition raw data
  // is not part of the progress rows). Keyed by exerciseResultId; value is
  // the transcript string, "" when the recognizer produced no text, and
  // undefined while still loading.
  let transcripts = {};

  async function loadDataProgress() {
    reportStatus = "LOADING";
    return fetch(
      BASE_URL +
        `/public/assessment/instance/` +
        instanceId +
        `/progress-report`,
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        report = buildIeltsSpeakingReport(data);
        reportStatus = "OK";
        if (variant === "DETAIL") {
          loadTranscripts();
        }
      })
      .catch((error) => {
        console.error("catch error", error);
        reportStatus = "ERROR";
      });
  }

  function recognitionToTranscript(rawData) {
    try {
      const parts = JSON.parse(rawData);
      if (!Array.isArray(parts)) return "";
      return parts
        .map((part) => part?.DisplayText)
        .filter((text) => typeof text === "string" && text.trim().length)
        .join(" ");
    } catch (error) {
      return "";
    }
  }

  function loadTranscripts() {
    report.answers
      .filter((answer) => answer.exerciseResultId)
      .forEach((answer) => {
        fetch(
          BASE_URL +
            `/public/assessment/instance/` +
            instanceId +
            `/result/` +
            answer.exerciseResultId,
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error("Network response was not ok");
            }
            return response.json();
          })
          .then((result) => {
            transcripts = {
              ...transcripts,
              [answer.exerciseResultId]: recognitionToTranscript(
                result?.rawData,
              ),
            };
          })
          .catch((error) => {
            console.error("catch error", error);
            transcripts = { ...transcripts, [answer.exerciseResultId]: "" };
          });
      });
  }

  onMount(() => {
    loadDataProgress();
  });

  function toggleAnswer(answerNumber) {
    collapsedAnswers = {
      ...collapsedAnswers,
      [answerNumber]: !collapsedAnswers[answerNumber],
    };
  }
</script>

<div class="container ielts-report paper-shadow">
  {#if reportStatus === "LOADING"}
    <Loader />
  {:else if reportStatus === "ERROR"}
    <div class="text-center p-5">{$_("ERROR_LOADING_ASSESSMENT")}</div>
  {:else}
    <IeltsReportHeader
      {instanceData}
      band={report.band}
      skill="SPEAKING"
    />

    {#if variant !== "DETAIL"}
      <div class="mb-4 mt-5">
        <hr data-content={$_("SCORE_SUMMARY")} class="hr-text" />
      </div>

      <div class="row justify-content-center">
        <div class="col-md-4 col-6 text-center summary-box">
          <div class="summary-value">
            {report.answeredCount} / {report.answers.length}
          </div>
          <div class="summary-label">{$_("ANSWERS")}</div>
        </div>
        <div class="col-md-4 col-6 text-center summary-box">
          <div class="summary-value">{formatIeltsBand(report.band)}</div>
          <div class="summary-label">{$_("IELTS_SPEAKING_BAND")}</div>
        </div>
      </div>
    {/if}

    {#if variant === "DETAIL"}
      <div class="mb-2 mt-5">
        <h4 class="section-title">{$_("DETAILED_FEEDBACK")}</h4>
        <hr />
      </div>

      {#each report.answers as answer (answer.number)}
        <div class="mb-4 ielts-avoid-break">
          <IeltsReportItemHeader
            number={answer.number}
            title={answer.title}
            value={answer.band === null ? null : formatIeltsBand(answer.band)}
            label={$_("BAND")}
            emptyText={$_("NOT_ANSWERED")}
            collapsed={collapsedAnswers[answer.number]}
            toggle={() => toggleAnswer(answer.number)}
          />

          <!-- The header spans the full width; the body splits in two: what
               the candidate was asked on the left, what they said and how it
               scored on the right. -->
          <div
            class="ielts-collapsible-body"
            class:collapsed={collapsedAnswers[answer.number]}
          >
            <div class="row mt-3">
              <div class="col-lg-6 mb-3">
                <h6 class="column-title">{$_("QUESTION")}</h6>
                {#if answer.prompt}
                  <div class="prompt-box">
                    <div class="prompt-text">
                      {@html sanitizeAssessmentHtml(answer.prompt)}
                    </div>
                  </div>
                {:else}
                  <div class="text-muted small">-</div>
                {/if}
              </div>

              <div class="col-lg-6 mb-3">
                {#if answer.audioUrl}
                  <div class="audio-box mb-3">
                    <span class="column-title mr-3">{$_("AUDIO")}</span>
                    <audio controls preload="none">
                      <source src={answer.audioUrl} type="audio/ogg" />
                      <track default kind="captions" srclang="en" src="" />
                      {$_("NOT_SUPPORTED")}
                    </audio>
                  </div>
                {/if}

                {#if answer.exerciseResultId}
                  <div class="transcript-box mb-3">
                    <div class="transcript-label">{$_("TRANSCRIPT")}</div>
                    {#if transcripts[answer.exerciseResultId] === undefined}
                      <div class="text-muted small">...</div>
                    {:else if transcripts[answer.exerciseResultId]}
                      <div class="transcript-text">
                        {transcripts[answer.exerciseResultId]}
                      </div>
                    {:else}
                      <div class="text-muted small">
                        {$_("TRANSCRIPT_NOT_AVAILABLE")}
                      </div>
                    {/if}
                  </div>
                {/if}

                <h6 class="column-title mb-0">{$_("DETAIL_SCORING")}</h6>
                <p class="text-muted small mb-2">
                  {$_("DETAIL_SCORING_DESCRIPTION")}
                </p>
                <div class="score-detail ielts-avoid-break">
                  <div class="score-row">
                    <span>{$_("OVERALL")}</span>
                    <span class="score-band">{formatIeltsBand(answer.band)}</span
                    >
                  </div>
                  {#if answer.scoreTotal !== null && answer.scoreTotalMax !== null}
                    <div class="score-row">
                      <span>{$_("SCORE")}</span>
                      <span class="score-raw"
                        >{answer.scoreTotal} / {answer.scoreTotalMax}</span
                      >
                    </div>
                  {/if}
                  {#each answer.scoreDetail as criterion (criterion.key)}
                    <div class="score-row">
                      <span>{$_("IELTS_SPEAKING_" + criterion.key)}</span>
                      <span class="score-band"
                        >{formatIeltsBand(criterion.band)}</span
                      >
                    </div>
                  {/each}
                </div>
              </div>
            </div>
          </div>
        </div>
      {/each}
    {/if}

    <div class="row justify-content-center mt-4 report-footer-link">
      <a
        href="/"
        on:click={() => {
          $currentPage = "";
        }}>assessment.oksensay.com</a
      >
    </div>
  {/if}
</div>

<style>
  @media (max-width: 768px) {
    .container {
      padding: 0;
    }
  }

  .summary-box {
    padding: 1rem;
  }

  .summary-value {
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
  }

  .summary-label {
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #9ca3af;
  }

  hr.hr-text {
    position: relative;
    border: none;
    height: 1px;
    background: #999;
  }

  hr.hr-text::before {
    content: attr(data-content);
    display: inline-block;
    background: #fff;
    font-weight: bold;
    font-size: 0.85rem;
    color: #999;
    border-radius: 30rem;
    padding: 0.2rem 2rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .section-title {
    font-weight: 700;
    color: #1f2937;
  }

  .column-title {
    font-weight: 700;
    color: #374151;
  }

  .prompt-box {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 1rem 1.25rem;
  }

  .prompt-text {
    font-size: 0.875rem;
    color: #4b5563;
    line-height: 1.6;
  }

  .score-detail {
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    padding: 0.25rem 0.75rem;
  }

  .score-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.4rem 0;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #374151;
  }

  .score-row + .score-row {
    border-top: 1px solid #f3f4f6;
  }

  .score-band {
    font-size: 1rem;
    font-weight: 700;
    color: #0056b3;
  }

  .score-raw {
    font-size: 0.9rem;
    font-weight: 700;
    color: #4b5563;
  }

  .audio-box {
    display: flex;
    align-items: center;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.5rem 1rem;
  }

  .audio-box audio {
    flex: 1;
    max-width: 30rem;
    height: 2.25rem;
  }

  .transcript-box {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
  }

  .transcript-label {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.65rem;
    color: #6b7280;
    margin-bottom: 0.25rem;
  }

  .transcript-text {
    font-size: 0.875rem;
    color: #1f2937;
    line-height: 1.7;
  }

  /* Wider than Bootstrap's .container cap, with tighter side padding, so the
     report uses the width a full-HD screen actually has. */
  .paper-shadow {
    position: relative;
    background: #fff;
    max-width: 1500px;
    padding: 3rem 2rem;
    margin: 50px auto;
    box-shadow: 0px 2px 38px rgba(0, 0, 0, 0.2);
  }
  .paper-shadow:after,
  .paper-shadow:before {
    content: "";
    position: absolute;
    left: auto;
    background: none;
    z-index: -1;
  }
  .paper-shadow:after {
    width: 90%;
    height: 10px;
    top: 30px;
    right: 8px;
    transform: rotate(-3deg);
    box-shadow: 0px -25px 35px 0px rgba(0, 0, 0, 0.5);
  }

  .paper-shadow:before {
    width: 10px;
    height: 95%;
    top: 5px;
    right: 18px;
    transform: rotate(3deg);
    box-shadow: 22px 0px 35px 0px rgba(0, 0, 0, 0.5);
  }
</style>
